/**
 * Oyun İçi Chat Komponenti
 * Oyuncular arası mesajlaşma ve emoji tepkileri
 */
import { useState, useRef, useEffect } from "react";
import { MessageCircle, Send, Smile, X, ChevronDown } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { cn } from "../../lib/utils";

const QUICK_EMOJIS = ["👍", "😄", "😢", "🎉", "😮", "🔥", "💪", "👏"];

export default function GameChat({ 
  messages = [], 
  onSendMessage, 
  onSendReaction,
  currentUserId,
  isMinimized = false,
  onToggleMinimize
}) {
  const [message, setMessage] = useState("");
  const [showEmojis, setShowEmojis] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Yeni mesaj geldiğinde scroll
  useEffect(() => {
    if (!isMinimized && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
    if (isMinimized && messages.length > 0) {
      setUnreadCount(prev => prev + 1);
    }
  }, [messages, isMinimized]);

  // Minimize açıldığında unread sıfırla
  useEffect(() => {
    if (!isMinimized) {
      setUnreadCount(0);
    }
  }, [isMinimized]);

  const handleSend = (e) => {
    e.preventDefault();
    if (message.trim()) {
      onSendMessage?.(message.trim());
      setMessage("");
    }
  };

  const handleEmojiClick = (emoji) => {
    onSendReaction?.(emoji);
    setShowEmojis(false);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
  };

  // Minimized görünüm
  if (isMinimized) {
    return (
      <button
        onClick={onToggleMinimize}
        className="fixed bottom-16 right-4 z-[100] flex items-center gap-2 px-4 py-3 rounded-full bg-[#0A1635] border border-[#4DFFFF]/30 shadow-lg hover:border-[#4DFFFF]/50 transition-colors"
        data-testid="chat-toggle-btn"
      >
        <MessageCircle className="w-5 h-5 text-[#4DFFFF]" />
        <span className="text-white text-sm font-medium">Sohbet</span>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>
    );
  }

  return (
    <div 
      className="fixed bottom-16 right-4 z-[100] w-80 bg-[#0A1635]/95 backdrop-blur-sm border border-white/10 rounded-xl shadow-2xl flex flex-col"
      style={{ maxHeight: "400px" }}
      data-testid="game-chat"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-[#4DFFFF]" />
          <span className="text-white font-medium text-sm">Oyun Sohbeti</span>
        </div>
        <button
          onClick={onToggleMinimize}
          className="p-1 hover:bg-white/10 rounded"
        >
          <ChevronDown className="w-4 h-4 text-white/60" />
        </button>
      </div>

      {/* Messages */}
      <div 
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-3 space-y-2"
        style={{ minHeight: "200px", maxHeight: "280px" }}
      >
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-8">
            <MessageCircle className="w-8 h-8 text-white/20 mb-2" />
            <p className="text-white/40 text-sm">Henüz mesaj yok</p>
            <p className="text-white/30 text-xs">İlk mesajı gönder!</p>
          </div>
        ) : (
          messages.map((msg, idx) => (
            <div key={idx}>
              {msg.type === "chat" ? (
                <div className={cn(
                  "flex flex-col",
                  msg.user_id === currentUserId ? "items-end" : "items-start"
                )}>
                  <div className={cn(
                    "max-w-[85%] px-3 py-2 rounded-lg",
                    msg.user_id === currentUserId 
                      ? "bg-[#4DFFFF]/20 text-white" 
                      : "bg-white/10 text-white",
                    msg.is_spectator && "border border-dashed border-white/20"
                  )}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={cn(
                        "text-xs font-medium",
                        msg.user_id === currentUserId ? "text-[#4DFFFF]" : "text-[#C6A87C]"
                      )}>
                        {msg.name}
                        {msg.is_spectator && <span className="ml-1 text-white/40">(Seyirci)</span>}
                      </span>
                      <span className="text-[10px] text-white/40">
                        {formatTime(msg.timestamp)}
                      </span>
                    </div>
                    <p className="text-sm break-words">{msg.message}</p>
                  </div>
                </div>
              ) : msg.type === "reaction" ? (
                <div className="flex items-center justify-center gap-1 py-1">
                  <span className="text-xs text-white/50">{msg.name}</span>
                  <span className="text-lg animate-bounce">{msg.emoji}</span>
                </div>
              ) : msg.type === "system" ? (
                <div className="text-center py-1">
                  <span className="text-xs text-[#4DFFFF]/70 bg-[#4DFFFF]/10 px-2 py-1 rounded">
                    {msg.message}
                  </span>
                </div>
              ) : null}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Emoji Bar */}
      {showEmojis && (
        <div className="px-3 py-2 border-t border-white/10 bg-white/5">
          <div className="flex items-center justify-between gap-1">
            {QUICK_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                onClick={() => handleEmojiClick(emoji)}
                className="text-xl hover:scale-125 transition-transform p-1"
                data-testid={`emoji-${emoji}`}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSend} className="p-3 border-t border-white/10">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowEmojis(!showEmojis)}
            className={cn(
              "p-2 rounded-lg transition-colors",
              showEmojis ? "bg-[#4DFFFF]/20 text-[#4DFFFF]" : "text-white/60 hover:text-white hover:bg-white/10"
            )}
          >
            <Smile className="w-5 h-5" />
          </button>
          <Input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Mesaj yaz..."
            className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/40 h-9"
            maxLength={500}
            data-testid="chat-input"
          />
          <Button
            type="submit"
            size="sm"
            disabled={!message.trim()}
            className="bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/90 h-9 px-3"
            data-testid="chat-send-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </form>
    </div>
  );
}
